import axios from "axios";

const client = axios.create({
  baseURL: "/api",
  timeout: 10000,
  withCredentials: true, // Include cookies for IAP authentication
});

import { OktaAuth, AccessToken } from '@okta/okta-auth-js';

// Store the OktaAuth instance - will be set by setOktaAuth
let oktaAuthInstance: OktaAuth | null = null;

export const setOktaAuth = (oktaAuth: OktaAuth) => {
  oktaAuthInstance = oktaAuth;
};

// Add request interceptor for logging in development and injecting auth token
client.interceptors.request.use(async (config) => {
  // Inject Okta token if available
  if (oktaAuthInstance) {
    try {
      const accessToken = await oktaAuthInstance.tokenManager.get('accessToken') as AccessToken;
      if (accessToken && accessToken.accessToken) {
        if (import.meta.env.DEV) {
          console.log('✓ Injecting Okta token:', accessToken.accessToken.substring(0, 10) + '...');
        }
        config.headers.Authorization = `Bearer ${accessToken.accessToken}`;
      } else {
        if (import.meta.env.DEV) {
          console.warn('⚠ No access token found in tokenManager');
        }
      }
    } catch (e) {
      if (import.meta.env.DEV) {
        console.warn('⚠ Failed to get Okta token', e);
      }
    }
  } else {
    if (import.meta.env.DEV) {
      console.warn('⚠ oktaAuthInstance not set yet - API call may fail');
    }
  }

  if (import.meta.env.DEV) {
    console.log('→ API Request:', config.method?.toUpperCase(), config.url);
  }
  return config;
});

// Add response interceptor for error handling
client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (import.meta.env.DEV) {
      console.error('API Error:', error.response?.status, error.response?.data);
    }
    return Promise.reject(error);
  }
);

// Helper function for authenticated fetch (for file uploads, blob downloads, etc.)
export const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  // Get Okta token if available
  if (oktaAuthInstance) {
    try {
      const accessToken = await oktaAuthInstance.tokenManager.get('accessToken') as AccessToken;
      if (accessToken && accessToken.accessToken) {
        // Add Authorization header
        options.headers = {
          ...options.headers,
          'Authorization': `Bearer ${accessToken.accessToken}`,
        };
      }
    } catch (e) {
      if (import.meta.env.DEV) {
        console.warn('⚠ Failed to get Okta token for fetch', e);
      }
    }
  }

  // Make the fetch call with credentials
  return fetch(url, {
    ...options,
    credentials: 'include', // Include cookies for IAP fallback
  });
};

/**
 * Get the current Okta access token string, or null if unavailable.
 */
export const getAccessToken = async (): Promise<string | null> => {
  if (!oktaAuthInstance) return null;
  try {
    const token = await oktaAuthInstance.tokenManager.get('accessToken') as AccessToken;
    return token?.accessToken ?? null;
  } catch {
    return null;
  }
};

export default client;
